import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

const cardStyles = cva("bg-white rounded-xl border transition-all duration-200", {
    variants: {
        variant: {
            default: "border-gray-200 shadow-soft",
            elevated: "border-gray-100 shadow-medium",
            outlined: "border-gray-300 shadow-none",
            ghost: "border-transparent shadow-none",
        },
        padding: {
            none: "p-0",
            sm: "p-4",
            md: "p-6",
            lg: "p-8",
        },
        hover: {
            true: "cursor-pointer hover:shadow-medium hover:border-gray-300",
            false: "",
        },
    },
    defaultVariants: {
        variant: "default",
        padding: "md",
        hover: false,
    },
});

export interface CardProps extends VariantProps<typeof cardStyles> {
    children: ReactNode;
    className?: string;
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}

const ACTIVATION_KEYS = new Set(["Enter", " "]);

export default function Card({ children, variant, padding, hover, className, onClick }: CardProps) {
    const isInteractive = Boolean(onClick);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!onClick || !ACTIVATION_KEYS.has(event.key)) return;
        onClick(event as unknown as MouseEvent<HTMLDivElement>);
    };

    return (
        <div
            className={cn(cardStyles({ variant, padding, hover }), className)}
            onClick={onClick}
            role={isInteractive ? "button" : undefined}
            tabIndex={isInteractive ? 0 : undefined}
            onKeyDown={isInteractive ? handleKeyDown : undefined}
        >
            {children}
        </div>
    );
}
